import React from 'react';
import './Pagination.css';

interface PaginationProps {
  currentPage: number;
  totalItems?: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onPageChange: (page: number) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
  loading?: boolean;
}

/**
 * Pagination Component
 * Requirements: 10.4 - Efficient pagination for large expense lists
 */
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  hasNextPage,
  hasPreviousPage,
  onPageChange,
  onNextPage,
  onPreviousPage,
  loading = false
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems || currentPage * itemsPerPage);

  const handlePageInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const target = event.target as HTMLInputElement;
      const page = parseInt(target.value, 10);
      if (page && page > 0) {
        onPageChange(page);
      }
    }
  };

  return (
    <div className="pagination">
      <div className="pagination-info">
        {totalItems ? (
          <span>
            Showing {startItem}-{endItem} of {totalItems} items
          </span>
        ) : (
          <span>
            Page {currentPage} ({itemsPerPage} items per page)
          </span>
        )}
      </div>

      <div className="pagination-controls">
        <button
          className="pagination-button"
          onClick={onPreviousPage}
          disabled={!hasPreviousPage || loading}
          title="Previous page"
        >
          ← Previous
        </button>

        <div className="pagination-page-info">
          <span>Page</span>
          <input
            type="number"
            min="1"
            defaultValue={currentPage}
            onKeyDown={handlePageInput}
            className="pagination-page-input"
            disabled={loading}
          />
        </div>

        <button
          className="pagination-button"
          onClick={onNextPage}
          disabled={!hasNextPage || loading}
          title="Next page"
        >
          Next →
        </button>
      </div>

      {loading && (
        <div className="pagination-loading">
          <span>Loading...</span>
        </div>
      )}
    </div>
  );
};

export default Pagination;